(() => {
  const config = window.kukkiAiImageBadge || {};
  const badgeLabel = config.label || 'AI generated';
  const allowedEffects = ['subtle', 'balanced', 'strong', 'solid'];
  const badgeEffect = allowedEffects.includes(config.effect) ? config.effect : 'balanced';
  const configuredFontSize = Number(config.fontSize);
  const badgeFontSize = Number.isFinite(configuredFontSize)
    ? Math.min(18, Math.max(9, configuredFontSize))
    : 11;
  const configuredTextOffsetY = Number(config.textOffsetY);
  const badgeTextOffsetY = Number.isFinite(configuredTextOffsetY)
    ? Math.min(4, Math.max(-4, configuredTextOffsetY))
    : -1;
  const configuredSlideshowTextOffsetY = Number(config.slideshowTextOffsetY);
  const slideshowBadgeTextOffsetY = Number.isFinite(configuredSlideshowTextOffsetY)
    ? Math.min(4, Math.max(-4, configuredSlideshowTextOffsetY))
    : 1;
  const configuredBackgroundOpacity = Number(config.backgroundOpacity);
  const badgeBackgroundOpacity = Number.isFinite(configuredBackgroundOpacity)
    ? Math.min(100, Math.max(0, configuredBackgroundOpacity)) / 100
    : 0.4;
  const configuredBackdropBlur = Number(config.backdropBlur);
  const badgeBackdropBlur = Number.isFinite(configuredBackdropBlur)
    ? Math.min(30, Math.max(0, configuredBackdropBlur))
    : 12;

  const hexToRgba = (hexColor, alpha) => {
    const normalized = String(hexColor || '#141414').replace('#', '').trim();
    const expanded = normalized.length === 3
      ? normalized.split('').map((character) => character + character).join('')
      : normalized;
    const validHex = /^[0-9a-f]{6}$/i.test(expanded) ? expanded : '141414';
    const red = parseInt(validHex.slice(0, 2), 16);
    const green = parseInt(validHex.slice(2, 4), 16);
    const blue = parseInt(validHex.slice(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  };

  document.documentElement.style.setProperty('--ai-image-badge-font-size', `${badgeFontSize}px`);
  document.documentElement.style.setProperty('--ai-image-badge-background-color', config.backgroundColor || '#141414');
  document.documentElement.style.setProperty('--ai-image-badge-background', hexToRgba(config.backgroundColor, badgeBackgroundOpacity));
  document.documentElement.style.setProperty('--ai-image-badge-backdrop-blur', `${badgeBackdropBlur}px`);
  document.documentElement.style.setProperty('--ai-image-badge-text-color', config.textColor || '#ffffff');
  document.documentElement.style.setProperty('--ai-image-badge-text-offset-y', `${badgeTextOffsetY}px`);
  document.documentElement.style.setProperty('--ai-image-badge-slideshow-text-offset-y', `${slideshowBadgeTextOffsetY}px`);
  const controlTokenPattern = /ai_g(?:e)?nerated_(top|bottom)/i;
  const legacyExactPattern = /^\s*ai_g(?:e)?nerated\s*$/i;
  const excludedImageSelector = [
    '.header__logo-image',
    '.image-icon',
    '.logo-list__image',
    '.press__image',
    '.slideshow__icon-image',
    '.comment__gravatar',
    '[data-ai-badge="off"] img',
    'img[data-ai-badge="off"]'
  ].join(',');
  const hostSelector = [
    '.product-card__figure',
    '.product-gallery__media',
    '.blog-post-card__figure',
    '.content-over-media',
    '.collection-card__figure',
    'figure',
    'picture',
    'a',
    'div'
  ].join(',');
  const controlledImageSelector = [
    'img[alt*="ai_generated" i]',
    'img[alt*="ai_gnerated" i]',
    'img[data-ai-badge="top-right"]',
    'img[data-ai-badge="bottom-right"]'
  ].join(',');
  const badgeScopeSelector = '#main, [role="dialog"], [aria-modal="true"], dialog, .modal';
  const observerOptions = {
    attributes: true,
    attributeFilter: ['alt'],
    childList: true,
    subtree: true
  };
  let stylesheetPromise;

  const ensureStylesheet = () => {
    if (stylesheetPromise) return stylesheetPromise;

    stylesheetPromise = new Promise((resolve, reject) => {
      const existingStylesheet = document.querySelector('link[data-ai-image-badge-styles]');
      if (existingStylesheet) {
        resolve();
        return;
      }

      const stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = config.stylesheetUrl;
      stylesheet.dataset.aiImageBadgeStyles = '';
      stylesheet.addEventListener('load', resolve, { once: true });
      stylesheet.addEventListener('error', reject, { once: true });
      document.head.appendChild(stylesheet);
    });

    return stylesheetPromise;
  };

  const readBadgeControl = (image) => {
    if (image.dataset.aiBadge === 'top-right' || image.dataset.aiBadge === 'bottom-right') {
      return image.dataset.aiBadge;
    }

    const alt = image.getAttribute('alt') || '';
    const match = alt.match(controlTokenPattern);
    const isLegacyExact = legacyExactPattern.test(alt);

    if (!match && !isLegacyExact) return null;

    const position = match?.[1]?.toLowerCase() === 'top' ? 'top-right' : 'bottom-right';
    const cleanedAlt = isLegacyExact
      ? ''
      : alt
          .replace(new RegExp(controlTokenPattern.source, 'gi'), ' ')
          .replace(/\[\s*\]|\(\s*\)|\{\s*\}/g, ' ')
          .replace(/\s+([,;])/g, '$1')
          .replace(/[\s,;|]+$/g, '')
          .replace(/^\s*[,;|]+/g, '')
          .replace(/\s{2,}/g, ' ')
          .trim();

    image.setAttribute('alt', cleanedAlt);
    image.dataset.aiBadge = position;
    return position;
  };

  const mountBadge = (image, position) => {
    if (!image.isConnected || !image.closest(badgeScopeSelector)) return;
    if (image.matches(excludedImageSelector) || image.closest('[data-ai-badge="off"]')) return;

    let host = image.closest(hostSelector);
    if (host?.tagName === 'PICTURE') host = host.parentElement;
    if (!host || host.closest('.ai-image-badge')) return;
    if (host.querySelector(':scope > .ai-image-badge')) return;

    host.classList.add('ai-image-badge-host');

    const badge = document.createElement('span');
    badge.className = `ai-image-badge ai-image-badge--${position} ai-image-badge--effect-${badgeEffect}`;

    const labelParts = badgeLabel.trim().split(/\s+/);
    const lead = document.createElement('span');
    lead.className = 'ai-image-badge__lead';
    lead.textContent = labelParts.shift() || 'AI';
    badge.appendChild(lead);

    if (labelParts.length > 0) {
      const rest = document.createElement('span');
      rest.className = 'ai-image-badge__rest';
      rest.textContent = labelParts.join(' ');
      badge.appendChild(rest);
    }

    badge.setAttribute('aria-hidden', 'true');
    host.appendChild(badge);
  };

  const addBadge = (image) => {
    if (!(image instanceof HTMLImageElement) || !image.closest(badgeScopeSelector)) return;
    const position = readBadgeControl(image);
    if (!position) return;

    ensureStylesheet()
      .then(() => mountBadge(image, position))
      .catch(() => {});
  };

  const scan = (root = document) => {
    if (root instanceof HTMLImageElement && root.matches(controlledImageSelector)) addBadge(root);
    root.querySelectorAll?.(controlledImageSelector).forEach(addBadge);
  };

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        addBadge(mutation.target);
        return;
      }

      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) scan(node);
      });
    });
  });

  const init = () => {
    const main = document.querySelector('#main');
    if (!main) return;

    scan(main);
    observer.observe(main, observerOptions);

    document.addEventListener('dialog:after-show', (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLElement)) return;

      scan(dialog);
      observer.observe(dialog, observerOptions);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
